import React from 'react';
import Loading from '../../components/Loading';
import _t from 'counterpart';
import { fetchPropTradingAccountsReport } from '../../services/BackendService';
import { useQuery } from 'react-query';
import PaginationTable from '../../components/PaginationTable';
import { formatCurrency, formatDate, formatPercentage, isSet } from '../../helpers';

export interface IAnalyticsRecord {
  downloads: string;
  level: number;
  registrations: string;
  pushNotifications: string;
}

const PropTradingAccounts = ({ onFetch }: { onFetch: (count: number) => void }) => {
  const propTradingAccountsQuery = useQuery(
    ['reports', 'prop-trading-accounts'],
    () => fetchPropTradingAccountsReport(),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data: any) => {
        if (data) {
          onFetch(data.length);
        }
      },
      onError: () => onFetch(0),
    }
  );

  if (propTradingAccountsQuery.isLoading || propTradingAccountsQuery.isIdle) {
    return <Loading />;
  }

  if (propTradingAccountsQuery.isError) {
    return null;
  }

  const { data } = propTradingAccountsQuery;

  return (
    <PaginationTable
      tableFields={[
        { key: 'accountId', label: _t('customer.account-id'), sorter: false },
        { key: 'customer', label: _t('customer.table.name'), sorter: false },
        { key: 'customerCreatedAt', label: _t('customer.table.created-at'), sorter: false },
        { key: 'country', label: _t('customer.table.country'), sorter: false },
        { key: 'funnel', label: _t('prop-funnels.funnel'), sorter: false },
        { key: 'stage', label: _t('wallets.stage'), sorter: false },
        { key: 'status', label: _t('reports.prop-trading.status'), sorter: false },
        { key: 'group', label: _t('wallet-modal.group'), sorter: false },
        { key: 'leverage', label: _t('wallets.leverage'), sorter: false },
        { key: 'currency', label: _t('wallets.currency'), sorter: false },
        { key: 'initialBalance', label: _t('funded-account-types.initial-balance'), sorter: false },
        { key: 'balance', label: _t('reports.prop-trading.balance'), sorter: false },
        { key: 'floatingPL', label: _t('wallet.profit-loss'), sorter: false },
        { key: 'closedPL', label: _t('reports.prop-trading.closed-pl'), sorter: false },
        { key: 'equity', label: _t('wallet.equity'), sorter: false },
        { key: 'marginUsed', label: _t('wallet.margin'), sorter: false },
        { key: 'freeMargin', label: _t('wallet.free-margin'), sorter: false },
        { key: 'marginLevel', label: _t('wallet.margin-level'), sorter: false },
        { key: 'profitTarget', label: _t('reports.prop-trading.profit-target'), sorter: false },
        { key: 'remainingAmount', label: _t('reports.prop-trading.remaining-amount'), sorter: false },
        { key: 'remainingPercentage', label: _t('reports.prop-trading.remaining-percentage'), sorter: false },
        { key: 'accountAgeDays', label: _t('reports.prop-trading.account-age-days'), sorter: false },
        { key: 'tradingDays', label: _t('reports.prop-trading.trading-days'), sorter: false },
        { key: 'lastTradeDate', label: _t('reports.prop-trading.last-trade-date'), sorter: false },
      ]}
      scopedSlots={{
        accountId: (row: any) => (
          <td className="text-nowrap">
            <a href={`/wallets/${row.accountId}`}>{row.accountId}</a>
          </td>
        ),
        customer: (row: any) => {
          return (
            <td className="text-nowrap">
              <a href={`/customers/${row.customerId}`}>{row.customerName}</a>
            </td>
          );
        },
        funnel: (row: any) => {
          return (
            <td className="text-nowrap">
              <a href={`/prop-funnels/${row.funnel}`}>{row.funnel}</a>
            </td>
          );
        },
        stage: (row: any) => {
          return <td className="text-nowrap">{row.currentStage ? `${row.currentStage} / ${row.maxStage}` : '-'}</td>;
        },
        country: (row: any) => <td className="text-nowrap">{row.country}</td>,
        customerCreatedAt: (row: any) => <td className="text-nowrap">{formatDate(new Date(row.customerCreatedAt))}</td>,
        status: (row: any) => <td className="text-nowrap">{row.status}</td>,
        group: (row: any) => <td className="text-nowrap">{row.group}</td>,
        leverage: (row: any) => <td className="text-nowrap">{row.leverage}</td>,
        currency: (row: any) => <td className="text-nowrap">{row.currency}</td>,
        initialBalance: (row: any) => (
          <td className="text-nowrap">{isSet(row.initialBalance) ? formatCurrency(row.initialBalance) : '-'}</td>
        ),
        balance: (row: any) => (
          <td className="text-nowrap">{isSet(row.balance) ? formatCurrency(row.balance) : '-'}</td>
        ),
        floatingPL: (row: any) => (
          <td className="text-nowrap">{isSet(row.floatingPL) ? formatCurrency(row.floatingPL) : '-'}</td>
        ),
        closedPL: (row: any) => <td className="text-nowrap">{row.closedPL ? formatCurrency(row.closedPL) : '-'}</td>,
        equity: (row: any) => <td className="text-nowrap">{isSet(row.equity) ? formatCurrency(row.equity) : '-'}</td>,
        marginUsed: (row: any) => (
          <td className="text-nowrap">{isSet(row.marginUsed) ? formatCurrency(row.marginUsed) : '-'}</td>
        ),
        freeMargin: (row: any) => (
          <td className="text-nowrap">{isSet(row.marginFree) ? formatCurrency(row.marginFree) : '-'}</td>
        ),
        marginLevel: (row: any) => (
          <td className="text-nowrap">
            {isSet(row.marginLevelPercentage) ? formatPercentage(row.marginLevelPercentage) : '-'}
          </td>
        ),
        profitTarget: (row: any) => (
          <td className="text-nowrap">{isSet(row.profitTarget) ? formatCurrency(row.profitTarget) : '-'}</td>
        ),
        remainingAmount: (row: any) => (
          <td className="text-nowrap">{isSet(row.remainingAmount) ? formatCurrency(row.remainingAmount) : '-'}</td>
        ),
        remainingPercentage: (row: any) => (
          <td className="text-nowrap">
            {isSet(row.remainingPercentage) ? formatPercentage(row.remainingPercentage) : '-'}
          </td>
        ),
        accountAgeDays: (row: any) => <td className="text-nowrap">{row.accountAgeDays}</td>,
        tradingDays: (row: any) => <td className="text-nowrap">{row.tradingDays}</td>,
        lastTradeDate: (row: any) => (
          <td className="text-nowrap">{row.lastTradeDate ? formatDate(new Date(row.lastTradeDate)) : '-'}</td>
        ),
      }}
      data={data || []}
      loading={propTradingAccountsQuery.isLoading}
      pages={0}
    />
  );
};

export default PropTradingAccounts;
